import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CalendarClock } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  ChartCard,
  ChartState,
  MetricCard,
  formatMoneyCLP,
  formatNumber,
} from "@/components/bi/BiPrimitives";
import { estadoMPLabel, tipoMPCorto } from "@/lib/mercado-publico-catalog";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/plazos")({
  head: () => ({ meta: [{ title: "Reporte Plazos — LicitaPro" }] }),
  component: Page,
});

const BUCKET_LABELS: Record<string, string> = {
  hoy: "Cierran hoy",
  "3d": "Próximos 3 días",
  "7d": "Próximos 7 días",
  "30d": "Próximos 30 días",
};

function formatDate(value: string | null) {
  if (!value) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function daysLeft(value: string | null) {
  if (!value) return null;
  const diff = new Date(value).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

function Page() {
  const { filters } = useBiFilters();
  const deadlines = useQuery({
    queryKey: ["bi", "deadlines", filters],
    queryFn: () => biAnalyticsService.deadlines(filters),
  });
  const d = deadlines.data;

  return (
    <AppShell
      title="Plazos y vencimientos"
      subtitle="Ventanas de postulación críticas dentro del periodo filtrado"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        {(d?.buckets ?? [{ key: "hoy", count: 0 }, { key: "3d", count: 0 }, { key: "7d", count: 0 }, { key: "30d", count: 0 }]).map(
          (bucket) => (
            <MetricCard
              key={bucket.key}
              label={BUCKET_LABELS[bucket.key] ?? bucket.key}
              value={formatNumber(bucket.count)}
              icon={CalendarClock}
              loading={deadlines.isLoading}
            />
          ),
        )}
        <MetricCard
          label="Ya vencidas"
          value={formatNumber(d?.expired ?? 0)}
          icon={AlertTriangle}
          loading={deadlines.isLoading}
        />
        <MetricCard
          label="Sin fecha de cierre"
          value={formatNumber(d?.withoutDate ?? 0)}
          loading={deadlines.isLoading}
        />
      </div>

      <ChartCard
        title="Cierres críticos"
        subtitle="Licitaciones vigentes que cierran en los próximos 7 días"
      >
        <ChartState
          loading={deadlines.isLoading}
          error={deadlines.error}
          empty={!d?.critical?.length}
          height={220}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Licitación</th>
                  <th className="text-left font-medium py-2">Organismo</th>
                  <th className="text-left font-medium py-2">Tipo</th>
                  <th className="text-left font-medium py-2">Estado MP</th>
                  <th className="text-right font-medium py-2">Monto</th>
                  <th className="text-right font-medium py-2">Cierre</th>
                </tr>
              </thead>
              <tbody>
                {(d?.critical ?? []).map((row) => {
                  const left = daysLeft(row.fechaCierre);
                  return (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      <td className="py-2 max-w-[280px]">
                        <Link
                          to={row.tipo === "compra-agil" ? "/compra-agil/$id" : "/licitaciones/$id"}
                          params={{ id: row.id }}
                          className="text-foreground hover:text-primary truncate block"
                        >
                          {row.nombre}
                        </Link>
                        <span className="text-xs text-muted-foreground font-mono">{row.codigo}</span>
                      </td>
                      <td className="py-2 text-muted-foreground truncate max-w-[180px]">
                        {row.organismo}
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {row.tipo === "compra-agil" ? "Compra Ágil" : tipoMPCorto(row.tipoMP)}
                      </td>
                      <td className="py-2 text-muted-foreground">{estadoMPLabel(row.estadoMP)}</td>
                      <td className="py-2 text-right tabular-nums text-foreground">
                        {row.montoEstimado != null && row.moneda === "CLP"
                          ? formatMoneyCLP(row.montoEstimado)
                          : row.montoEstimado != null
                            ? `${formatNumber(row.montoEstimado)} ${row.moneda}`
                            : "—"}
                      </td>
                      <td className="py-2 text-right text-xs">
                        <span className="text-foreground">{formatDate(row.fechaCierre)}</span>
                        {left != null && (
                          <span
                            className={
                              left <= 1 ? "block text-destructive" : "block text-muted-foreground"
                            }
                          >
                            {left === 0 ? "cierra hoy" : `en ${left} día${left === 1 ? "" : "s"}`}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ChartState>
      </ChartCard>
    </AppShell>
  );
}
